import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { cssUrl } from '../utils/cssVersion';
import { fetchSuperSlots, fetchSuperJobs, assignSuperJob, superJobAction, fetchSnlJobs, createSnlJobApi, patchSnlJobStatus, patchSnlJobFields, resyncStatuses, fetchSuperThreads, sendSuperMessage, markSuperThreadSeen } from '../utils/api';
import usePollWhenVisible from '../utils/usePollWhenVisible';
import MessagesPanel, { ThreadModal, useLockBody } from '../components/MessagesPanel';
import { FoldGroup, FOLD_PEEK } from '../components/shared/FoldGroup';
import { useToasts, ToastStack } from '../components/Toasts';
import { successMatch } from '../utils/successMatch';
import { useUser } from '../utils/UserContext';
import { melTodayISO, fmtShort, timeToLabel, addDays, endOfWeek, startOfWeek } from '../utils/melDate';
import CustomSelect from '../components/shared/CustomSelect';
import { findSuburb, roadKm, homeBaseForPostcode } from '../data/vicSuburbs';
import SuburbField from '../components/SuburbField';
import DateField from '../components/DateField';
/* desktop (≥1025px) → table rows per date; below → cards */
const DESKTOP_QUERY = '(min-width: 1025px)';
const useIsDesktop = () => {
  const [is, setIs] = useState(() => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const on = (e) => setIs(e.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  return is;
};

/* Home Base coordinates for a slot: the stored postcode → vicSuburbs centroid */
const homeCoords = (slot) => homeBaseForPostcode(slot.homeBase?.postcode);

/**
 * SupervisorAvailability
 *
 *   <SupervisorProvider>          fetches slots + jobs, holds actions
 *     SuperSlots                  Open Slots board → Assign modal (Pending job)
 *     SuperResponses              Pending / Accepted / Declined → Approve / Cancel
 *     SuperRoster                 Approved by date → Cancel
 *
 * Wrap InnerTabsd / InnerTabsm in the provider on the d / m pages; the three
 * tab components render with no props and read from context.
 * Loads casualsAvailability.css (shared buttons, pills, modal shell) plus
 * supervisorAvailability.css.
 */

const ACCENT = '#22d3ee';               // supervisor accent
const STATUS_LABEL = { pending: 'Pending', accepted: 'Accepted', declined: 'Declined', approved: 'Done', cancelled: 'Withdrawn' };
const NO_PIPELINE_JOB = 'Select Job';

const Ctx = createContext(null);
export const useSuper = () => useContext(Ctx);

const useStylesheets = (hrefs) => {
  const hrefKey = hrefs.join('|');            // stable primitive — `hrefs` is a new array every render
  useEffect(() => {
    hrefs.forEach((href) => {
      const key = href.split('?')[0];
      if (document.querySelector(`link[data-sv-css="${key}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = href; link.dataset.svCss = key;
      document.head.appendChild(link);
    });
    // `hrefs` itself can't be a dependency (new array each render → runs every render);
    // hrefKey is its content, which is what we actually want to react to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hrefKey]);
};

const telHref = (m) => `tel:${String(m || '').replace(/\s+/g, '')}`;

/* Address + suburb, but never "…, Docklands, Docklands": the suburb is only
   appended when the address doesn't already end with / contain it. */
export const addrLine = (address, suburb) => {
  const a = String(address || '').trim().replace(/,\s*$/, '');
  const sb = String(suburb || '').trim();
  if (!sb) return a;
  if (!a) return sb;
  return a.toLowerCase().includes(sb.toLowerCase()) ? a : `${a}, ${sb}`;
};

/* one-shot submit button: disabled + spinner while busy */
const BusyButton = ({ busy, label, busyLabel, onClick, className = 'ca-btn solid', style, disabled = false, title }) => (
  <button type="button" className={`${className} ${busy ? 'is-busy' : ''}`} style={style} title={title}
          disabled={busy || disabled} aria-busy={busy} onClick={() => { if (!busy && !disabled) onClick(); }}>
    {busy && <span className="sv-spin" aria-hidden="true" />}
    {busy ? busyLabel : label}
  </button>
);

/* ─────────────────────────────────────────────────────────────
   Success Match — the scoring itself lives in utils/successMatch.js so the
   Assign modal and the Match Availability board score identically.

   scoreSlotJob() is the adapter: it pulls CB/CP/JB/JP, the home→job km and
   the two time windows out of an availability slot + a pipeline job.
   ───────────────────────────────────────────────────────────── */
const jobSuburbOf = (j) =>
  findSuburb(`${j.jobSuburb || ''} ${j.jobPostcode || ''}`) || findSuburb(j.jobSuburb)
  || (j.jobSuburb && j.jobLat != null && j.jobLng != null
    ? { s: j.jobSuburb, p: j.jobPostcode || '', la: Number(j.jobLat), lo: Number(j.jobLng), km: null, custom: true }
    : null);

const scoreSlotJob = (slot, jobSub, { jobStart, jobEnd, casualStart, casualEnd, clash }) => {
  const home = homeCoords(slot);
  const km = jobSub && home ? roadKm(home, jobSub) : null;
  return successMatch({
    casualStart: casualStart ?? slot.start,
    casualEnd: casualEnd ?? slot.end,
    jobStart, jobEnd,
    // CB / CP are the suburb the casual ASKED to work in, not where they live —
    // how far they live from it is already priced in by Travel below.
    casualBase: slot.location?.suburb,
    casualPostcode: slot.location?.postcode,
    jobBase: jobSub?.s,
    jobPostcode: jobSub?.p,
    km,
    radiusKm: slot.radius,
    clash,                       // set → Hours scores 0; must be forwarded, not dropped
  });
};

/* the note shown on hover for a whole score */
const matchTitle = (m) => m.parts.map((x) => `${x.label} ${x.got}/${x.of} — ${x.note}`).join('\n');

/* ── the badge, reused by the Assign header and the Match board ── */
const MatchBadge = ({ label, value, pct, band, title, size }) => (
  <div className={`sv-match ${size ? `sv-match-${size}` : ''} sv-match-${band}`} title={title}>
    <span className="sv-match-label">{label}</span>
    <span className="sv-match-value">{value}</span>
    <span className="sv-match-bar" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></span>
  </div>
);

/* Job name options are labelled "Hella · 17 Sep 2026" */
const longDate = (iso) => { const [y, m, d] = iso.split('-').map(Number); return `${d} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1]} ${y}`; };
const jobLabelOf = (j) => `${j.jobName} · ${j.jobStartDate ? longDate(j.jobStartDate) : 'no date'}`;

/* A casual needs time to get from one job to the next, so two jobs on the
   same day must be at least MIN_GAP_MIN apart — not merely non-overlapping.
   12:00-15:00 after a job ending 12:00 is a clash; 12:30 is the earliest it
   can start. The same rule is enforced in profile.py's _clashing_job. */
export const MIN_GAP_MIN = 30;
const addMin = (hhmm, n) => {
  if (!hhmm) return hhmm;
  const [h, m] = hhmm.split(':').map(Number);
  const t = h * 60 + m + n;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};
const overlaps = (aStart, aEnd, bStart, bEnd, gap = MIN_GAP_MIN) =>
  !!(aStart && aEnd && bStart && bEnd && aStart < addMin(bEnd, gap) && addMin(aEnd, gap) > bStart);

/* A job section in the Job Area — collapsed by default once there is an
   existing job to show, so a busy day opens tidy rather than as a wall. */
const JobSection = ({ n, title, status, score, focused, open, onToggle, msgCount = null, warn = '', children }) => (
  <div className={`sv-jobsec sv-wide ${open ? 'is-open' : ''} ${focused ? 'is-focused' : ''}`}>
    <button type="button" className="sv-jobsec-head" onClick={onToggle} aria-expanded={open}>
      <svg className="sv-jobsec-caret" width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
      <span className="sv-jobsec-n">Job {n}</span>
      <span className="sv-jobsec-title">{title}</span>
      {warn && <span className="sv-jobsec-warn">{warn}</span>}
      {msgCount != null && <span className="sv-jobsec-msgs">{msgCount} message{msgCount === 1 ? '' : 's'}</span>}
      {/* each job carries its OWN score, so it is never in doubt which job
          the big badges at the top are describing */}
      {score && <span className={`sv-jobsec-score sv-match-${score.band}`} title={matchTitle(score)}>{score.pct}%</span>}
      {status && <span className={`ca-status ${status} sv-jobsec-status`}>{STATUS_LABEL[status] || status}</span>}
    </button>
    {open && <div className="sv-jobsec-body sv-form">{children}</div>}
  </div>
);

/* A pipeline job → the Assign form's fields. Shared by the Job name dropdown
   and by a row handed over from the Match Availability board. */
const jobToForm = (j) => ({
  pipelineJobId: j.id, jobName: j.jobName, jobLabel: jobLabelOf(j), jobType: j.jobType || '',
  jobAddress: j.jobAddress || '',
  jobStart: j.jobStart || '',           // the job's own hours, NOT clamped to the
  jobFinish: j.jobEnd || '',            // casual's window — the casual has their own
  contactPerson: j.contactPerson || '', contactPhone: j.contactMobile || '', notes: j.notes || '',
});

/* Match Availability's one-click Assign: the same payload the Assign form builds from a picked
   pipeline job — job fields from jobToForm, suburb from the job (else the day's suburb). Returns
   { payload } or { error } for what the form would have refused on submit. */
const matchAssignPayload = (slot, job) => {
  const f = jobToForm(job);
  const jobSub = jobSuburbOf(job) || findSuburb(slot.location?.suburb) || null;
  if (!jobSub) return { error: 'Pick a job suburb for this job under Create Jobs first' };
  if (!String(f.jobAddress || '').trim()) return { error: 'Job address is required — add it under Create Jobs' };
  if (!f.jobStart || !f.jobFinish || f.jobFinish <= f.jobStart) return { error: 'Job finish must be after job start — fix the job under Create Jobs' };
  return { payload: {
    pipelineJobId: f.pipelineJobId || undefined, jobName: f.jobName, jobType: f.jobType,
    jobAddress: f.jobAddress, jobSuburb: jobSub.s || slot.location?.suburb, jobPostcode: jobSub.p || slot.location?.postcode,
    jobLat: jobSub.la, jobLng: jobSub.lo,
    jobStart: f.jobStart, jobFinish: f.jobFinish,
    contactPerson: f.contactPerson, contactPhone: f.contactPhone, notes: f.notes,
  } };
};

/* ─────────────────────────────────────────────────────────────
   Assign modal — one slot → one Pending job.

   The card is a flex column capped at 82vh: header (sticky) and footer are
   fixed, and everything between them scrolls in ONE region capped at 60vh.
   `min-height: 0` on that region is what makes the scroll actually work — a
   flex child defaults to min-height:auto and refuses to shrink below its
   content, which is why the fields spilled out of the card before.
   ───────────────────────────────────────────────────────────── */
const AssignModal = ({ slot, onClose, onAssign, pipelineJobs = [], history = [], onOpenHistory, initialJob = null }) => {
  const openMessage = useSuper()?.openMessage;         // "Send message" on the stacked Job 1 … N sections
  const threads = useSuper()?.threads || [];
  const msgCountOf = (id) => (threads.find((t) => t.assignmentId === id)?.messages?.length) || 0;
  const slotSub = findSuburb(slot.location?.suburb) || null;
  const EMPTY_FORM = {
    pipelineJobId: '', jobName: '', jobLabel: '', jobType: '',
    jobAddress: '',
    jobStart: '', jobFinish: '',         // filled from the job record, not the casual
    contactPerson: '', contactPhone: '', notes: '',
  };
  const [form, setForm] = useState(initialJob ? { ...EMPTY_FORM, ...jobToForm(initialJob) } : EMPTY_FORM);
  const [jobSub, setJobSub] = useState((initialJob && jobSuburbOf(initialJob)) || slotSub || null);

  /* Jobs already live on this day become read-only Job 1 … Job N; the one
     being added is the next number up. Declined / withdrawn ones are history
     and stay in the list at the foot of the modal. */
  const liveJobs = history.filter((h) => ['pending', 'accepted', 'approved'].includes(h.status));
  const newJobNo = liveJobs.length + 1;

  /* Only jobs dated for EXACTLY this slot's day can be assigned — a casual's
     availability is for one date, so the two start dates must match.

     A job that cannot leave MIN_GAP_MIN either side of one already on the day
     can never be assigned, so it is filtered out of the dropdown rather than
     offered and then refused on submit. */
  const datedJobs = pipelineJobs
    .filter((j) => j.status === 'Available' && j.jobStartDate === slot.date)
    .sort((a, b) => a.jobName.localeCompare(b.jobName));
  const blockedJobs = datedJobs.filter((j) => liveJobs.some((h) => overlaps(j.jobStart, j.jobEnd, h.jobStart, h.jobFinish)));
  const dayJobs = datedJobs.filter((j) => !blockedJobs.includes(j));
  const jobNameOptions = [NO_PIPELINE_JOB, ...dayJobs.map(jobLabelOf)];
  const pickPipelineJob = (label) => {
    const j = dayJobs.find((x) => jobLabelOf(x) === label);
    if (!j) { setForm((f) => ({ ...f, pipelineJobId: '', jobName: '', jobLabel: '' })); return; }
    // Job Suburb follows the pipeline job when it has one (matched to the list, else
    // kept as a custom entry with the job's own coordinates); the supervisor can still change it
    const jobSuburb = jobSuburbOf(j);
    if (jobSuburb) setJobSub(jobSuburb);
    setForm((f) => ({ ...f, ...jobToForm(j) }));
  };
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // collapsed by default — except on an empty day, where Job 1 IS the form
  const [openJob, setOpenJob] = useState(liveJobs.length === 0 ? newJobNo : 0);
  const toggle = (n) => setOpenJob((cur) => (cur === n ? 0 : n));

  /* the new job cannot overlap one already on the day */
  const clashJob = liveJobs.find((h) => overlaps(form.jobStart, form.jobFinish, h.jobStart, h.jobFinish)) || null;
  const clashNote = clashJob
    ? `clashes with Job ${liveJobs.indexOf(clashJob) + 1}, ${timeToLabel(clashJob.jobStart)} – ${timeToLabel(clashJob.jobFinish)}`
      + ` — leave at least ${MIN_GAP_MIN} min between jobs`
    : '';

  /* the live job that finishes LAST — the one a new job has to come after.
     history is ordered by status, not by time, so it is found not assumed. */
  const lastOut = liveJobs.reduce((best, h, i) => (
    !best.job || (h.jobFinish && h.jobFinish > best.job.jobFinish) ? { job: h, n: i + 1 } : best
  ), { job: null, n: 0 });

  /* every live job gets its own score against this casual */
  const liveScores = liveJobs.map((h) => scoreSlotJob(slot, jobSuburbOf(h), { jobStart: h.jobStart, jobEnd: h.jobFinish }));

  /* What to say under a job's message button. It only nags when there is
     something to chase — a job the casual cannot fully cover, or (once there
     are two) a pair whose combined span runs past the hours they gave. */
  const hoursOf = (sc) => sc?.parts.find((x) => x.key === 'hours');
  const casualWindow = `${timeToLabel(slot.start)} – ${timeToLabel(slot.end)}`;
  const shortJobs = liveJobs.filter((h, i) => { const x = hoursOf(liveScores[i]); return x && x.got < x.of; });
  const spanStart = liveJobs.reduce((a, h) => (!a || (h.jobStart && h.jobStart < a) ? h.jobStart : a), '');
  const spanEnd = liveJobs.reduce((a, h) => (!a || (h.jobFinish && h.jobFinish > a) ? h.jobFinish : a), '');

  /* the job the casual cannot fully cover — its Hours part scores under full marks */
  const isShort = (i) => { const x = hoursOf(liveScores[i]); return !!(x && x.got < x.of); };
  const jobHint = (i) => {
    const h = liveJobs[i];
    const hrs = `${timeToLabel(h.jobStart)} – ${timeToLabel(h.jobFinish)}`;
    if (liveJobs.length >= 2) {
      /* with two jobs on the day the casual's hours have to carry both */
      const list = liveJobs.map((x, n) => `Job ${n + 1} ${timeToLabel(x.jobStart)} – ${timeToLabel(x.jobFinish)}`).join(' and ');
      if (shortJobs.length === 0) {
        return `${list} both sit inside the casual's ${casualWindow}, at least ${MIN_GAP_MIN} min apart.`;
      }
      /* the amber warning goes ONLY on the job the casual cannot fully cover — the one the message
         is about. The jobs that fit say nothing. */
      if (!isShort(i)) return '';
      return `${list} together need ${timeToLabel(spanStart)} – ${timeToLabel(spanEnd)}, but the casual is only available ${casualWindow}. Message them to widen their hours for this day.`;
    }
    if (!isShort(i)) return '';                         // nothing to chase
    return `This job runs ${hrs} but the casual is only available ${casualWindow}. Message them to widen their hours for this day, or withdraw the job.`;
  };
  /* the fold's amber cue: only on a job whose section carries the amber warning */
  const jobWarn = (i) => (jobHint(i) && shortJobs.length ? 'Read warning' : '');

  /* The hours the day as a whole demands once this job is added, and whether
     the casual's own window stretches that far. This is what the supervisor
     has to get the casual to agree to — and it is the same figure the offer
     email quotes and the casual's Accept button is gated on. */
  const newJobReady = !!(form.jobStart && form.jobFinish);
  const pendingWindows = [...liveJobs.map((h) => [h.jobStart, h.jobFinish]),
    ...(newJobReady ? [[form.jobStart, form.jobFinish]] : [])].filter(([a, b]) => a && b);
  const needStart = pendingWindows.reduce((a, [x]) => (!a || x < a ? x : a), '');
  const needEnd = pendingWindows.reduce((a, [, y]) => (!a || y > a ? y : a), '');
  const dayShort = !!(needStart && needEnd
    && ((slot.start && slot.start > needStart) || (slot.end && slot.end < needEnd)));

  /* Which job the big header badges describe. An OPEN live job wins; failing
     that it is the job being added, but only once a job has actually been
     picked for it. Merely opening an empty Job 2 must not drag the score to
     0/40 — there is nothing to score yet, so Job 1 keeps the badges. */
  const jobInFocus = (openJob && openJob <= liveJobs.length)
    ? openJob
    : (newJobReady ? newJobNo : (liveJobs.length || newJobNo));

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();

  // Job start / finish are read-only: they come from the job record the
  // supervisor picks (clamped to the casual's window by pickPipelineJob).
  const home = homeCoords(slot);                              // Richmond (home base), not the slot suburb
  const homeName = slot.homeBase?.suburb || home?.suburb || 'home';
  const km = jobSub && home ? roadKm(home, jobSub) : null;    // home base → job suburb
  const outside = km != null && slot.radius != null && km > slot.radius;
  const homeToSlotKm = slot.estimatedKm ?? (home && slotSub ? roadKm(home, slotSub) : null);
  const newJobMatch = scoreSlotJob(slot, jobSub, {
    jobStart: form.jobStart, jobEnd: form.jobFinish,
    casualStart: slot.start, casualEnd: slot.end,
    clash: clashNote,
  });
  const match = jobInFocus <= liveJobs.length ? liveScores[jobInFocus - 1] : newJobMatch;

  const submit = async () => {
    if (busy) return;
    setErr('');
    if (clashNote) { setErr(`This job ${clashNote}. Pick hours that don't overlap, or message the casual to move that job.`); return; }
    if (!jobSub) { setErr('Pick a job suburb from the list or press Look up'); return; }
    if (!form.jobAddress.trim()) { setErr('Job address is required'); return; }
    if (!form.jobStart || !form.jobFinish || form.jobFinish <= form.jobStart) { setErr('Job finish must be after job start'); return; }
    setBusy(true);
    try {
      await onAssign(slot.id, {
        pipelineJobId: form.pipelineJobId || undefined, jobName: form.jobName, jobType: form.jobType,
        jobAddress: form.jobAddress, jobSuburb: jobSub?.s || slot.location?.suburb, jobPostcode: jobSub?.p || slot.location?.postcode,
        jobLat: jobSub?.la, jobLng: jobSub?.lo,
        jobStart: form.jobStart, jobFinish: form.jobFinish,
        contactPerson: form.contactPerson, contactPhone: form.contactPhone, notes: form.notes,
      });
      onClose();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec sv-assign sv-assign-modal" role="dialog" aria-modal="true" aria-label="Assign job">
        <div className="ca-rec-head sv-assign-head">
          <div className="ca-rec-title">Assign job<span className="ca-rec-dot"> · </span><span className="ca-rec-dates">{fmtShort(slot.date)}</span></div>
          <div className="sv-match-row">
            <MatchBadge label={liveJobs.length ? `Match · Job ${jobInFocus}` : 'Match'}
              value={`${match.pct}%`} pct={match.pct} band={match.band} title={matchTitle(match)} />
            {match.parts.map((x) => (
              <MatchBadge key={x.key} size="sm" label={x.label} value={`${x.got}/${x.of}`}
                pct={(x.got / x.of) * 100} band={x.band} title={`${x.label} ${x.got}/${x.of} — ${x.note}`} />
            ))}
          </div>
        </div>

        <div className="sv-assign-scroll">
        <div className="ca-rec-body sv-form">
          {/* ── Job ───────────────────────────────────────────── */}
          <div className="sv-section sv-wide"><span className="sv-section-label">Job</span></div>

          {/* Jobs already offered on this day — read-only, collapsed */}
          {liveJobs.map((h, i) => (
            <JobSection
              key={h.id}
              n={i + 1}
              title={`${h.jobName || h.jobAddress || 'Job'} · ${timeToLabel(h.jobStart)} – ${timeToLabel(h.jobFinish)}`}
              status={h.status}
              score={liveScores[i]}
              warn={jobWarn(i)}
              msgCount={msgCountOf(h.id)}
              focused={jobInFocus === i + 1}
              open={openJob === i + 1}
              onToggle={() => toggle(i + 1)}
            >
              <label className="sv-field"><span className="ca-label">Job start</span>
                <input className="ca-readonly" value={timeToLabel(h.jobStart) || ''} readOnly /></label>
              <label className="sv-field"><span className="ca-label">Job end</span>
                <input className="ca-readonly" value={timeToLabel(h.jobFinish) || ''} readOnly /></label>
              <label className="sv-field sv-wide"><span className="ca-label">Job name</span>
                <input className="ca-readonly" value={h.jobName || ''} readOnly /></label>
              <label className="sv-field sv-wide"><span className="ca-label">Job type</span>
                <input className="ca-readonly" value={h.jobType || ''} readOnly /></label>
              <label className="sv-field sv-wide"><span className="ca-label">Job address</span>
                <input className="ca-readonly" value={addrLine(h.jobAddress, h.jobSuburb)} readOnly /></label>
              <label className="sv-field"><span className="ca-label">Contact person</span>
                <input className="ca-readonly" value={h.contactPerson || ''} readOnly /></label>
              <label className="sv-field"><span className="ca-label">Contact phone</span>
                <input className="ca-readonly" value={h.contactPhone || ''} readOnly /></label>
              <label className="sv-field sv-wide"><span className="ca-label">Notes for the casual</span>
                <textarea className="ca-readonly" rows={2} value={h.notes || ''} readOnly /></label>
              <div className="sv-wide sv-jobsec-actions">
                {/* straight into this job's thread — same button as the Responses tab's record */}
                <button type="button" className="ca-btn solid" style={{ background: ACCENT }}
                        title={`Message ${h.casualName || slot.casual.name || 'the casual'} about this job`}
                        onClick={() => (openMessage ? openMessage(h) : onOpenHistory?.(h))}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                  Send message
                </button>
                {jobHint(i) && <span className={`sv-km ${shortJobs.length ? 'sv-km-out' : ''}`}>{jobHint(i)}</span>}
              </div>
            </JobSection>
          ))}

          {/* The job being added */}
          <JobSection
            n={newJobNo}
            title={form.jobName ? `${form.jobName} · ${timeToLabel(form.jobStart)} – ${timeToLabel(form.jobFinish)}` : 'not set yet'}
            score={form.jobStart && form.jobFinish ? newJobMatch : null}
            focused={jobInFocus === newJobNo}
            open={openJob === newJobNo}
            onToggle={() => toggle(newJobNo)}
          >
          {clashNote && <div className="ca-error sv-wide">This job {clashNote}. Pick hours that don't overlap.</div>}
          {!clashNote && newJobReady && dayShort && (
            <div className="sv-need sv-wide">
              <b>
                {pendingWindows.length === 1 ? 'This job needs'
                  : pendingWindows.length === 2 ? 'Both jobs on this day need'
                  : `All ${pendingWindows.length} jobs on this day need`}
                {' '}{timeToLabel(needStart)} – {timeToLabel(needEnd)}.
              </b>
              {' '}{slot.casual.name || 'The casual'} gave {timeToLabel(slot.start)} – {timeToLabel(slot.end)}.
              {' '}The casual will need to widen their availability time, or won't be able to accept any job on this day until this is done.
            </div>
          )}

          {/* 1 · job name — picking one fills every read-only field below */}
          <div className="sv-field sv-wide ob-field">
            <CustomSelect label={`Job name · ${fmtShort(slot.date)}`} value={form.jobLabel || NO_PIPELINE_JOB} onChange={pickPipelineJob} options={jobNameOptions} />
            {dayJobs.length === 0 && (
              <div className="sv-km sv-km-out">
                {datedJobs.length === 0
                  ? <>No available jobs dated {fmtShort(slot.date)} — a job must start on the same day as the availability. Add one under Create Jobs.</>
                  : <>No jobs available after Job {lastOut.n} finishes at {timeToLabel(lastOut.job?.jobFinish)} — {MIN_GAP_MIN} min is needed between jobs.</>}
              </div>
            )}
            {dayJobs.length > 0 && liveJobs.length > 0 && (
              <div className="sv-km sv-km-ok">
                {dayJobs.length} job{dayJobs.length === 1 ? '' : 's'} fit{dayJobs.length === 1 ? 's' : ''} around Job {newJobNo} — {dayJobs.map((j) => `${j.jobName} ${timeToLabel(j.jobStart)} – ${timeToLabel(j.jobEnd)}`).join(', ')}
              </div>
            )}
          </div>

          {/* 2 · job hours — read-only, they belong to the job record */}
          <label className="sv-field">
            <span className="ca-label">Job start</span>
            <input className="ca-readonly" value={timeToLabel(form.jobStart) || ''} readOnly placeholder="Set by the job you select" />
          </label>
          <label className="sv-field">
            <span className="ca-label">Job end</span>
            <input className="ca-readonly" value={timeToLabel(form.jobFinish) || ''} readOnly placeholder="Set by the job you select" />
          </label>

          {/* 3 · job type */}
          <label className="sv-field sv-wide">
            <span className="ca-label">Job type</span>
            <input className="ca-readonly" value={form.jobType || ''} readOnly placeholder="Set by the job you select" />
          </label>

          {/* 4 · job address */}
          <label className="sv-field sv-wide">
            <span className="ca-label">Job address</span>
            <input value={form.jobAddress} onChange={(e) => set('jobAddress')(e.target.value)} placeholder="12 Harbour Esplanade" />
          </label>

          {/* 5 · job suburb — the Look up button lives inside SuburbField */}
          <div className="sv-field sv-wide ob-field">
            <SuburbField
              label="Job suburb"
              value={jobSub}
              onChange={setJobSub}
              address={form.jobAddress}
            />
            {km != null && (
              <div className={`sv-km ${outside ? 'sv-km-out' : ''}`}>
                {slot.homeBase?.suburb || slot.location?.suburb} to {jobSub.s} is roughly {km} km{outside ? ` — outside the ${slot.radius} km the casual gave` : ''}
              </div>
            )}
          </div>

          {/* 6 · who the casual reports to on the day */}
          <label className="sv-field">
            <span className="ca-label">Contact person</span>
            <input value={form.contactPerson} onChange={(e) => set('contactPerson')(e.target.value)} />
          </label>
          <label className="sv-field">
            <span className="ca-label">Contact phone</span>
            <input value={form.contactPhone} onChange={(e) => set('contactPhone')(e.target.value)} inputMode="tel" />
          </label>

          {/* 7 · notes */}
          <label className="sv-field sv-wide">
            <span className="ca-label">Notes for the casual</span>
            <textarea rows={3} value={form.notes} onChange={(e) => set('notes')(e.target.value)} />
          </label>

          </JobSection>

          <div className="sv-split sv-wide" role="separator" />

          {/* ── Casual — read-only: this is what they told us ── */}
          <div className="sv-section sv-wide"><span className="sv-section-label">Casual</span></div>

          {/* 1 · where they want to work */}
          <label className="sv-field sv-wide">
            <span className="ca-label">{slot.casual.name || 'Casual'} · Preferred suburb</span>
            <input className="ca-readonly" value={[slot.location?.suburb, slot.location?.postcode].filter(Boolean).join(' ')} readOnly />
          </label>

          {/* 2 · the window they offered — read-only: these times belong to the
              casual and are changed from their own Work Availability page */}
          <label className="sv-field">
            <span className="ca-label">Start time</span>
            <input className="ca-readonly" value={timeToLabel(slot.start) || ''} readOnly />
          </label>
          <label className="sv-field">
            <span className="ca-label">End time</span>
            <input className="ca-readonly" value={timeToLabel(slot.end) || ''} readOnly />
          </label>

          {/* 3 · how they're employed + how far home is from the suburb they chose */}
          <label className="sv-field">
            <span className="ca-label">Employed</span>
            <input className="ca-readonly" value={slot.casual.workPosition || ''} readOnly placeholder="Not recorded" />
          </label>
          <label className="sv-field">
            <span className="ca-label">Distance from home to work</span>
            <input className="ca-readonly" value={homeToSlotKm != null ? `${homeToSlotKm} km` : ''} readOnly placeholder="Not known" />
            <div className="sv-km">
              {homeName} → {slot.location?.suburb || 'work'}{slot.radius != null && <> · will travel up to {slot.radius} km</>}
            </div>
          </label>

          {/* 4 · how to reach them */}
          <label className="sv-field">
            <span className="ca-label">Email</span>
            <input className="ca-readonly" value={slot.casual.email || ''} readOnly placeholder="Not recorded" />
          </label>
          <label className="sv-field">
            <span className="ca-label">Mobile</span>
            <input className="ca-readonly" value={slot.casual.mobile || ''} readOnly placeholder="Not recorded" />
          </label>
          {err && <div className="ca-error sv-wide">{err}</div>}
        </div>

        {/* declined / withdrawn only — the live ones are Job 1 … N above */}
        {history.filter((h) => !['pending', 'accepted', 'approved'].includes(h.status)).length > 0 && (
          <div className="ca-rec-body sv-rec-assign">
            <div className="sv-panel-title">Earlier jobs on this day <span className="sv-day-count">{history.filter((h) => !['pending', 'accepted', 'approved'].includes(h.status)).length}</span></div>
            {history.filter((h) => !['pending', 'accepted', 'approved'].includes(h.status)).map((h) => (
              <div key={h.id} className="sv-rec-assign-row" onClick={() => onOpenHistory?.(h)} role="button" tabIndex={0}>
                <div>
                  <div className="sv-job-name">{h.jobName || h.jobAddress || 'Job'}</div>
                  <div className="sv-td-sub">{timeToLabel(h.jobStart)} – {timeToLabel(h.jobFinish)} · {addrLine(h.jobAddress, h.jobSuburb)}</div>
                  {h.declineReason && <div className="sv-td-sub sv-reason">Declined: {h.declineReason}</div>}
                  {h.cancelReason && <div className="sv-td-sub sv-reason">Withdrawn: {h.cancelReason}</div>}
                  <div className="sv-td-sub">Click to view · send message</div>
                </div>
                <span className={`ca-status ${h.status}`}>{STATUS_LABEL[h.status] || h.status}</span>
              </div>
            ))}
          </div>
        )}
        </div>

        <div className="ca-rec-foot">
          <button type="button" className="ca-btn ghost" onClick={onClose}>Cancel</button>
          {/* nothing to send until a job is picked for this section — and never
              while its hours clash with one already on the day */}
          <BusyButton busy={busy} disabled={!!clashNote || !newJobReady}
            title={clashNote ? `This job ${clashNote}`
              : !newJobReady ? (dayJobs.length
                ? `Pick a job for Job ${newJobNo} first`
                : `No job can be assigned to this day yet`)
              : undefined}
            label="Send to casual" busyLabel="Sending…" onClick={submit}
            /* an inline background beats any CSS rule, so drop it when the
               button is off — otherwise it stays accent-bright while dead */
            style={(!!clashNote || !newJobReady) ? undefined : { background: ACCENT }} />
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Job detail modal (read-only, all fields) — Responses, Roster, feed
   ───────────────────────────────────────────────────────────── */
const Field = ({ label, value, wide }) => (
  <div className={`sv-detail-field ${wide ? 'sv-wide' : ''}`}>
    <span className="ca-label">{label}</span>
    <div className="sv-detail-value">{value === '' || value == null ? <span className="sv-muted">—</span> : value}</div>
  </div>
);

export const JobDetailModal = ({ j, onClose, children }) => {
  const sup = useSuper();
  const openMessage = sup?.openMessage;
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();
  if (!j) return null;
  // the casual's window for the day: pre-joined by the Create Jobs feed (j.slot), else looked up
  // on the board by availability id (Responses / Roster); past days off the board show "—"
  // Every assignment carries it now (Slot Start / Slot End lookups), so it shows for Pending,
  // Accepted and Approved alike; older shapes fall back to the board.
  const win = (j.slotStart || j.slotEnd) ? { start: j.slotStart, end: j.slotEnd }
    : j.slot || (sup?.slots || []).find((s) => s.id === j.availabilityId) || {};
  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec sv-detail" role="dialog" aria-modal="true" aria-label="Job details">
        <div className="ca-rec-head">
          <div className="ca-rec-title">{j.jobName || j.jobAddress || 'Job'}<span className="ca-rec-dot"> · </span><span className="ca-rec-dates">{j.date ? fmtShort(j.date) : ''}</span></div>
          <div className="ca-rec-sub"><span className="ca-rec-home">{j.casualName || 'Not assigned'}</span>{j.jobType && <span className="sv-type-pill">{j.jobType}</span>}</div>
          <span className={`ca-status ${j.status} ca-rec-status`}>{STATUS_LABEL[j.status] || j.status}</span>
        </div>
        <div className="ca-rec-body sv-detail-grid">
          <Field label="Job address" value={addrLine(j.jobAddress, j.jobSuburb)} wide />
          <Field label="Job start" value={timeToLabel(j.jobStart)} />
          <Field label="Job finish" value={timeToLabel(j.jobFinish)} />
          <Field label="Casual" value={j.casualName} />
          <Field label="Work position" value={j.workPosition} />
          <Field label="Casual start" value={timeToLabel(win.start)} />
          <Field label="Casual end" value={timeToLabel(win.end)} />
          <Field label="Casual mobile" value={j.casualMobile} />   {/* plain text — no tap-to-call from the record */}
          <Field label="Casual available from" value={j.slotLocation ? `${j.slotLocation}${j.slotRadius != null ? `, ${j.slotRadius} km` : ''}` : ''} wide />
          <Field label="Contact person" value={j.contactPerson} />
          <Field label="Contact phone" value={j.contactPhone} />
          <Field label="Notes" value={j.notes} wide />
          {j.declineReason && <Field label="Declined because" value={j.declineReason} wide />}
          {j.cancelReason && <Field label="Cancelled because" value={j.cancelReason} wide />}
          <Field label="Assigned by" value={j.assignedBy} />
          <Field label="Assigned at" value={j.assignedAt ? new Date(j.assignedAt).toLocaleString('en-AU') : ''} />
          {j.approvedBy && <Field label="Approved by" value={j.approvedBy} />}
          {j.approvedAt && <Field label="Approved at" value={new Date(j.approvedAt).toLocaleString('en-AU')} />}
          {j.cancelledBy && <Field label="Cancelled by" value={j.cancelledBy} />}
        </div>
        {children}
        <div className="ca-rec-foot">
          {openMessage && j.casualName && (
            <button type="button" className="ca-btn solid" style={{ background: ACCENT }} title={`Message ${j.casualName} about this job`} onClick={() => openMessage(j)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              Send message
            </button>
          )}
          <button type="button" className="ca-btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Compact job row (Responses + Roster) — click to open the detail modal
   ───────────────────────────────────────────────────────────── */
const JobRow = ({ j, onOpen, onAction, showApprove, busy = '', msgCount = 0, allowWithdraw = true, onMessage = null }) => {
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);
  const locked = !!busy;
  const canApprove = showApprove && j.status === 'accepted';
  const canCancel = allowWithdraw && ['pending', 'accepted', 'approved'].includes(j.status);   // nothing to withdraw once declined / cancelled
  const stop = (e) => e.stopPropagation();
  return (
    <div className={`sv-roster-row sv-row-${j.status}`} onClick={() => onOpen(j)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onOpen(j)}>
      <div className="sv-roster-time">{timeToLabel(j.jobStart)}<br /><span className="sv-roster-end">to {timeToLabel(j.jobFinish)}</span></div>
      <div className="sv-roster-main">
        <div className="sv-job-name">{j.casualName || 'Unassigned'}<span className={`ca-status ${j.status} sv-row-status`}>{STATUS_LABEL[j.status] || j.status}</span></div>
        {j.workPosition && <div className="sv-position">{j.workPosition}</div>}
        <div className="sv-roster-addr">
          <span className="sv-roster-addr-text">{j.jobName ? `${j.jobName} · ` : ''}{addrLine(j.jobAddress, j.jobSuburb)}</span>
          {/* message total on the job-name line: right end, same colour as the name, left of the buttons */}
          <span className="sv-msg-count">{msgCount} message{msgCount === 1 ? '' : 's'}</span>
        </div>
        <div className="sv-roster-open">
          {(j.status === 'declined' || j.status === 'cancelled')
            ? <span className="sv-reassign">Job is back in the pipeline — assign another casual from Open Slots</span>
            : 'Click to view record ›'}
        </div>
      </div>
      <div className="sv-roster-actions" onClick={stop}>
        {asking ? (
          <>
            <input className="sv-reason-input" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button type="button" className="ca-btn ghost" onClick={() => setAsking(false)}>Back</button>
            <button type="button" className={`ca-btn decline ${busy === 'cancel' ? 'is-busy' : ''}`} disabled={locked} aria-busy={busy === 'cancel'} onClick={() => { onAction(j.id, 'cancel', reason); setAsking(false); }}>
              {busy === 'cancel' && <span className="sv-spin" aria-hidden="true" />}{busy === 'cancel' ? 'Processing…' : 'Confirm withdraw'}
            </button>
          </>
        ) : (
          <>
            {onMessage && (
              <button type="button" className="ca-btn solid" style={{ background: ACCENT }} title={`Message ${j.casualName || 'the casual'} about this job`} onClick={() => onMessage(j)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                Send message
              </button>
            )}
            {canCancel && (
              <button type="button" className={`ca-btn decline ${busy === 'cancel' ? 'is-busy' : ''}`} disabled={locked} aria-busy={busy === 'cancel'} title="Take this job back from the casual — it returns to the pipeline to assign again" onClick={() => setAsking(true)}>
                {busy === 'cancel' && <span className="sv-spin" aria-hidden="true" />}{busy === 'cancel' ? 'Processing…' : 'Withdraw'}
              </button>
            )}
            {canApprove && (
              <button type="button" className={`ca-btn accept ${busy === 'approve' ? 'is-busy' : ''}`} disabled={locked} aria-busy={busy === 'approve'} title="Mark the job as done — it moves to the Roster" onClick={() => onAction(j.id, 'approve')}>
                {busy === 'approve' && <span className="sv-spin" aria-hidden="true" />}{busy === 'approve' ? 'Processing…' : 'Approve (done)'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ── Job Messages, in the Open Slots toolbar ─────────────────────
   Same pill as the casual pages so the two sides read alike; sized to the
   chips beside it by the .sv-ranges rules in supervisorAvailability.css. */
const JobMessagesPill = ({ unread = 0, onClick }) => (
  <button type="button" className="ca-msgbtn" onClick={onClick}
    title={unread ? `${unread} unread job message${unread === 1 ? '' : 's'}` : 'Job messages'}>
    <span className="ca-msgbtn-label">Job Messages</span>
    <span className="ca-msgbtn-icon">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {unread > 0 && <span className="ca-msgbtn-dot">{unread}</span>}
    </span>
  </button>
);

const SuperMessagesModal = ({ onClose }) => {
  const { threads, threadsLoading, me, sendMessage, openThread, refreshThreads } = useSuper();
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();
  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec ca-msg-modal" role="dialog" aria-modal="true" aria-label="Job messages">
        <div className="ca-rec-head"><div className="ca-rec-title">Job messages</div></div>
        <div className="ca-msg-scroll">
          <MessagesPanel threads={threads} loading={threadsLoading} me={me} onSend={sendMessage}
            onOpen={openThread} onRefresh={refreshThreads} accent={ACCENT} showCasual side="super" />
        </div>
        <div className="ca-rec-foot"><button type="button" className="ca-btn ghost" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Match Availability — every casual × every Available job whose start date
   is the SAME day, scored with successMatch and ranked best first.

   The start dates matching is the primary filter: a casual's availability is
   for one date, so a job on another day can never be assigned to it.
   The four header badges are also the filter — click one to show only that
   grade, click again to clear.
   ───────────────────────────────────────────────────────────── */
const GRADES = [
  { id: 'good', label: 'Green 80+' },
  { id: 'ok', label: 'Amber 70–79' },
  { id: 'poor', label: 'Red 0–69' },
];

const ALL_CASUALS = 'All casuals';

/* Read-only details for one Match row: the pair's score badges in the header (the overall match
   and each scored part, as on the Assign form), the Job on top, the Casual beneath, Close below. */
const MatchInfoModal = ({ r, onClose, onEditNotes = null }) => {
  // Edit notes: the ONE editable thing here. Save patches the job's notes (live assignments follow on the
  // server), the button reads "Saving…" meanwhile, and the form stays open showing the new notes until
  // the supervisor closes it back to the Match board.
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(r.job.notes || '');        // what the form shows — updated after a save
  const [draft, setDraft] = useState(r.job.notes || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const saveNotes = async () => {
    if (saving) return;
    setErr('');
    setSaving(true);
    try { await onEditNotes(r.job.id, { notes: draft }); setNotes(draft.trim()); setEditing(false); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();
  const { slot, job, match } = r;
  const home = homeCoords(slot);
  const jobSub = jobSuburbOf(job);
  const km = jobSub && home ? roadKm(home, jobSub) : null;
  return (
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec sv-assign sv-assign-modal sv-detail sv-match-info" role="dialog" aria-modal="true" aria-label="Match details">
        <div className="ca-rec-head sv-assign-head">
          <div className="ca-rec-title">{job.jobName || 'Job'}<span className="ca-rec-dot"> · </span><span className="ca-rec-dates">{fmtShort(slot.date)}</span></div>
          <div className="ca-rec-sub"><span className="ca-rec-home">{slot.casual.name || 'Casual'}</span>{job.jobType && <span className="sv-type-pill">{job.jobType}</span>}</div>
          <div className="sv-match-row">
            <MatchBadge label="Match" value={`${match.pct}%`} pct={match.pct} band={match.band} title={matchTitle(match)} />
            {match.parts.map((x) => (
              <MatchBadge key={x.key} size="sm" label={x.label} value={`${x.got}/${x.of}`}
                pct={(x.got / x.of) * 100} band={x.band} title={`${x.label} ${x.got}/${x.of} — ${x.note}`} />
            ))}
          </div>
        </div>
        <div className="sv-assign-scroll">
        <div className="ca-rec-body sv-detail-grid">
          <div className="sv-section sv-wide"><span className="sv-section-label">Job</span></div>
          <Field label="Job name" value={job.jobName} />
          <Field label="Job type" value={job.jobType} />
          <Field label="Job date" value={job.jobStartDate ? fmtShort(job.jobStartDate) : ''} />
          <Field label="Job start – end" value={job.jobStart ? `${timeToLabel(job.jobStart)} – ${timeToLabel(job.jobEnd)}` : ''} />
          <Field label="Job address" value={addrLine(job.jobAddress, job.jobSuburb)} wide />
          <Field label="Contact person" value={job.contactPerson} />
          <Field label="Contact mobile" value={job.contactMobile} />
          {editing
            ? <label className="sv-field sv-wide"><span className="ca-label">Notes</span><textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} disabled={saving} /></label>
            : <Field label="Notes" value={notes ? <span style={{ whiteSpace: 'pre-line' }}>{notes}</span> : ''} wide />}
          {err && <div className="ca-error sv-wide">{err}</div>}
          <div className="sv-section sv-wide"><span className="sv-section-label">Casual</span></div>
          <Field label="Casual" value={slot.casual.name} />
          <Field label="Employed" value={slot.casual.workPosition} />
          <Field label="Available" value={`${timeToLabel(slot.start)} – ${timeToLabel(slot.end)}`} />
          <Field label="Preferred suburb" value={[slot.location?.suburb, slot.location?.postcode].filter(Boolean).join(' ')} />
          <Field label="Home base" value={slot.homeBase?.suburb ? `${slot.homeBase.suburb}${slot.radius != null ? ` · will travel up to ${slot.radius} km` : ''}` : ''} />
          <Field label="Home to job" value={km != null ? `${km} km${slot.radius != null && km > slot.radius ? ` — outside the ${slot.radius} km given` : ''}` : ''} />
          <Field label="Mobile" value={slot.casual.mobile} />
          <Field label="Email" value={slot.casual.email} />
          <Field label="Has WWCC" value={slot.casual.wwc ? 'Yes' : 'No'} />
        </div>
        </div>
        <div className="ca-rec-foot">
          {onEditNotes && !editing && (
            <button type="button" className="ca-btn sv-foot-left" onClick={() => { setDraft(notes); setErr(''); setEditing(true); }}>Edit notes</button>
          )}
          {editing && (
            <>
              <button type="button" className="ca-btn ghost" disabled={saving} onClick={() => { setEditing(false); setErr(''); }}>Cancel</button>
              <BusyButton busy={saving} label="Save notes" busyLabel="Saving…" onClick={saveNotes} style={{ background: ACCENT }} />
            </>
          )}
          <button type="button" className="ca-btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const MatchAvailabilityModal = ({ slots, pipelineJobs, liveJobs = [], onClose, onAssign, onEditNotes = null }) => {
  const [grade, setGrade] = useState('');            // '' = every record
  const [busyId, setBusyId] = useState(null);        // the pair being assigned right now
  const [info, setInfo] = useState(null);            // the row opened as a read-only form
  const [err, setErr] = useState('');
  // Assign submits straight away — no form. The button reads "Assigning…" until the server answers;
  // on success the job leaves the pipeline so its row drops out, and the board stays open for the next one.
  const assignNow = async (r) => {
    if (busyId) return;
    setErr('');
    const { payload, error } = matchAssignPayload(r.slot, r.job);
    const who = `${r.slot.casual.name || 'Casual'} · ${r.job.jobName}`;
    if (error) { setErr(`${who}: ${error}`); return; }
    setBusyId(r.id);
    try { await onAssign(r.slot, payload); }
    catch (e) { setErr(`${who}: ${e.message}`); }
    finally { setBusyId(null); }
  };
  const [casual, setCasual] = useState('');          // '' = every casual; the header picker narrows to one
  const names = useMemo(() => [...new Set(slots.map((s) => s.casual.name).filter(Boolean))].sort(), [slots]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();

  const allRows = useMemo(() => {
    const jobs = pipelineJobs.filter((j) => j.status === 'Available' && j.jobStartDate);
    const out = [];
    slots.forEach((slot) => {
      // a day already carrying a live job is only offered a job that still FITS beside it — the same
      // MIN_GAP_MIN rule the Assign form applies. A day with no time left drops out on its own; to give
      // its job to someone else the supervisor withdraws it, which reopens the day.
      const liveOn = liveJobs.filter((h) => h.availabilityId === slot.id);
      jobs.filter((j) => j.jobStartDate === slot.date && !liveOn.some((h) => overlaps(j.jobStart, j.jobEnd, h.jobStart, h.jobFinish))).forEach((j) => {
        const jobSub = jobSuburbOf(j);
        const m = scoreSlotJob(slot, jobSub, { jobStart: j.jobStart, jobEnd: j.jobEnd });
        out.push({ id: `${slot.id}-${j.id}`, slot, job: j, match: m });
      });
    });
    // date first — today at the top, later days beneath (the board only holds days from today on);
    // within a day the best score first, then the casual's name. Holds whether the list was just
    // loaded, narrowed by a filter, or re-scored after an assign.
    return out.sort((a, b) => a.slot.date.localeCompare(b.slot.date)
      || b.match.pct - a.match.pct
      || (a.slot.casual.name || '').localeCompare(b.slot.casual.name || ''));
  }, [slots, pipelineJobs, liveJobs]);
  const rows = useMemo(() => (casual ? allRows.filter((r) => r.slot.casual.name === casual) : allRows), [allRows, casual]);

  const counts = useMemo(() => rows.reduce((acc, r) => {
    acc[r.match.band] = (acc[r.match.band] || 0) + 1; return acc;
  }, {}), [rows]);
  const shown = grade ? rows.filter((r) => r.match.band === grade) : rows;

  return (
    <>
    <div className="ca-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec sv-assign sv-assign-modal sv-match-modal" role="dialog" aria-modal="true" aria-label="Match availability">
        <div className="ca-rec-head sv-assign-head">
          <div className="ca-rec-title">Match availability<span className="ca-rec-dot"> · </span><span className="ca-rec-dates">same start date</span></div>
          <div className="sv-match-row">
            <button type="button" className={`sv-match-btn ${grade === '' ? 'on' : ''}`} onClick={() => setGrade('')} title="Show every match">
              <MatchBadge label="Records" value={rows.length} pct={100} band="all" />
            </button>
            {GRADES.map((g) => (
              <button key={g.id} type="button" className={`sv-match-btn ${grade === g.id ? 'on' : ''}`}
                onClick={() => setGrade((x) => (x === g.id ? '' : g.id))} title={`Show only ${g.label}`}>
                <MatchBadge label={g.label} value={counts[g.id] || 0}
                  pct={rows.length ? ((counts[g.id] || 0) / rows.length) * 100 : 0} band={g.id} />
              </button>
            ))}
            {/* one casual, after the grade badges — the picker searches once the list is long */}
            <div className="sv-match-casual ob-field">
              <CustomSelect label="Casual" value={casual || ALL_CASUALS} onChange={(v) => setCasual(v === ALL_CASUALS ? '' : v)} options={[ALL_CASUALS, ...names]} />
            </div>
          </div>
        </div>

        <div className="sv-assign-scroll">
          {err && <div className="ca-error sv-match-err">{err}</div>}
          {shown.length === 0 ? (
            <div className="ca-empty">
              <div className="ca-empty-title">{rows.length === 0 ? (casual ? `Nothing to match for ${casual}` : 'Nothing to match yet') : 'No records at this grade'}</div>
              <p>{rows.length === 0
                ? 'A job and an availability day have to start on the same date before they can be matched. Add jobs under Create Jobs.'
                : 'Pick another grade above, or Records to see them all.'}</p>
            </div>
          ) : (
            <div className="sv-table-wrap sv-match-table-wrap">
              <table className="sv-table sv-match-table">
                <thead>
                  <tr>
                    <th>Casual</th>
                    <th>Casual start – end</th>
                    <th>Job name</th>
                    <th>Job start – end</th>
                    <th>Success</th>
                    <th aria-label="Assign" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.id} className="sv-tr" onClick={() => setInfo(r)} title="Click to view the job and the casual">
                      <td>
                        <div className="sv-td-name">{r.slot.casual.name || 'Casual'}</div>
                        <div className="sv-td-sub">{fmtShort(r.slot.date)}</div>
                        <div className="sv-match-open">Click to open ›</div>
                      </td>
                      <td>{timeToLabel(r.slot.start)} – {timeToLabel(r.slot.end)}</td>
                      <td>
                        <div>{r.job.jobName}</div>
                        {r.job.jobSuburb && <div className="sv-td-sub">{r.job.jobSuburb}</div>}
                      </td>
                      <td>{timeToLabel(r.job.jobStart)} – {timeToLabel(r.job.jobEnd)}</td>
                      <td className="sv-match-score">
                        <MatchBadge size="sm" label="Match" value={`${r.match.pct}%`} pct={r.match.pct}
                          band={r.match.band} title={matchTitle(r.match)} />
                      </td>
                      <td className="sv-match-assign" onClick={(e) => e.stopPropagation()}>
                        <BusyButton busy={busyId === r.id} disabled={!!busyId && busyId !== r.id}
                          label="Assign" busyLabel="Assigning…" className="ca-btn solid sv-btn-sm"
                          style={busyId && busyId !== r.id ? undefined : { background: ACCENT }}
                          onClick={() => assignNow(r)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="ca-rec-foot">
          <span className="sv-match-foot-count">{shown.length} of {rows.length} match{rows.length === 1 ? '' : 'es'}</span>
          <button type="button" className="ca-btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
    {info && <MatchInfoModal r={info} onClose={() => setInfo(null)} onEditNotes={onEditNotes} />}   {/* a sibling overlay stacks above */}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────
   Tab 1 — Open Slots
   ───────────────────────────────────────────────────────────── */
const RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'nextweek', label: 'Next week' },
  { id: 'all', label: 'All' },
];

export const SuperSlots = () => {
  const { slots, slotsLoading, today, assign, refreshSlots, snlJobs, responses, roster,
    unreadTotal, setMsgOpen, threads, openSlots, editSnlJob } = useSuper();
  const isDesktop = useIsDesktop();
  const [historyDetail, setHistoryDetail] = useState(null);
  // every job on a given availability day — active ones first, then declined / withdrawn
  const ORDER = { pending: 0, accepted: 1, approved: 2, declined: 3, cancelled: 4 };
  const historyFor = (slot) => [...responses, ...roster]
    .filter((j) => j.availabilityId === slot.id)
    .sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || (b.assignedAt || '').localeCompare(a.assignedAt || ''));

  // total messages across every job on this day — the Jobs cell reads "N pending · M messages"
  const msgCountFor = (slot) => historyFor(slot).reduce((n, j) => n + (((threads || []).find((t) => t.assignmentId === j.id) || {}).messages?.length || 0), 0);
  const [range, setRange] = useState('week');
  const [pickedDate, setPickedDate] = useState(null);      // ISO date from the DateField, or null = any
  const [q, setQ] = useState('');
  const [assigning, setAssigning] = useState(null);       // { slot, job? }
  const [matching, setMatching] = useState(false);

  // date filter: the dates that actually have availability, shown as "Fri 18 Sep"
  const dateOptions = useMemo(() => {
    const seen = [...new Set(slots.map((s) => s.date).filter(Boolean))].sort();
    return seen.map((d) => ({ iso: d, label: `${d === today ? 'Today · ' : ''}${fmtShort(d)}` }));
  }, [slots, today]);

  const filtered = useMemo(() => {
    let from = today, to = '9999-12-31';
    if (range === 'today') to = today;
    else if (range === 'week') to = endOfWeek(today);
    else if (range === 'nextweek') { from = addDays(startOfWeek(today), 7); to = addDays(from, 6); }
    const needle = q.trim().toLowerCase();
    return openSlots
      .filter((s) => (pickedDate ? s.date === pickedDate : s.date >= from && s.date <= to))
      .filter((s) => !needle || s.casual.name.toLowerCase().includes(needle) || (s.location?.suburb || '').toLowerCase().includes(needle))
      .sort((a, b) => a.date.localeCompare(b.date) || a.start.localeCompare(b.start) || a.casual.name.localeCompare(b.casual.name));
  }, [openSlots, range, q, pickedDate, today]);

  // how many open days each chip shows — the search box and date pick are not applied
  const rangeCount = useMemo(() => {
    const inRange = (s, id) => {
      if (id === 'today') return s.date === today;
      if (id === 'week') return s.date >= today && s.date <= endOfWeek(today);
      if (id === 'nextweek') { const from = addDays(startOfWeek(today), 7); return s.date >= from && s.date <= addDays(from, 6); }
      return s.date >= today;
    };
    return Object.fromEntries(RANGES.map((r) => [r.id, openSlots.filter((s) => inRange(s, r.id)).length]));
  }, [openSlots, today]);

  // the search box and the date pick narrow the match board too: type a casual (or suburb) and / or pick
  // a date first and only those days are scored. The range chips are NOT applied — with nothing typed or
  // picked the match board looks across every open day.
  const searchSlots = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return openSlots
      .filter((s) => !pickedDate || s.date === pickedDate)
      .filter((s) => !needle || s.casual.name.toLowerCase().includes(needle) || (s.location?.suburb || '').toLowerCase().includes(needle));
  }, [openSlots, q, pickedDate]);
  // the live jobs already on the board's days — Match Availability only offers a job that fits beside them
  const liveJobs = useMemo(() => [...responses, ...roster].filter((j) => ['pending', 'accepted', 'approved'].includes(j.status) && j.availabilityId), [responses, roster]);

  const byDate = useMemo(() => {
    const m = new Map();
    filtered.forEach((s) => { if (!m.has(s.date)) m.set(s.date, []); m.get(s.date).push(s); });
    return [...m.entries()];
  }, [filtered]);

  return (
    <div className="sv-pane">
      <div className="sv-toolbar">
        <div className="sv-ranges">
          {RANGES.map((r) => (
            <button key={r.id} type="button" className={`sv-chip ${range === r.id && !pickedDate ? 'on' : ''}`} style={range === r.id && !pickedDate ? { borderColor: ACCENT, color: ACCENT } : undefined} onClick={() => { setRange(r.id); setPickedDate(null); }}>{r.label}<span className="sv-chip-n">{rangeCount[r.id]}</span></button>
          ))}
          <button type="button" className="sv-chip sv-chip-match" onClick={() => setMatching(true)}
            title="Score every casual against every job starting the same day">Match Availability</button>
          <JobMessagesPill unread={unreadTotal} onClick={() => setMsgOpen(true)} />
        </div>
        <div className="sv-filters">
          <DateField className="sv-search" value={pickedDate} onChange={setPickedDate} options={dateOptions} placeholder="Any date" />
          <input className="sv-search" placeholder="Search casual or suburb" value={q} onChange={(e) => setQ(e.target.value)} />
          <button type="button" className="ca-btn ghost" onClick={refreshSlots}>Refresh</button>
        </div>
        {pickedDate && <div className="sv-hint">Showing {fmtShort(pickedDate)} only — pick a range above to clear.</div>}
      </div>


      {slotsLoading && slots.length === 0 && <div className="ca-skeleton" />}
      {!slotsLoading && byDate.length === 0 && (
        <div className="ca-empty"><div className="ca-empty-title">No availability in this range</div><p>Widen the range or pick another date.</p></div>
      )}

      {byDate.map(([date, list]) => (
        <section key={date} className="sv-day">
          <h3 className="sv-day-title">{date === today ? 'Today · ' : ''}{fmtShort(date)}<span className="sv-day-count">{list.length} casual{list.length === 1 ? '' : 's'}</span></h3>
          {isDesktop ? (
            <div className="sv-table-wrap">
              <table className="sv-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Available</th>
                    <th>Works From</th>
                    <th>Has WWCC</th>
                    <th>Jobs</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((s) => (
                    <React.Fragment key={s.id}>
                      <tr className="sv-tr" onClick={() => setAssigning({ slot: s })}>
                        <td>
                          <div className="sv-td-name">{s.casual.name || 'Casual'}</div>
                          {s.casual.mobile && <div className="sv-td-sub">{s.casual.mobile}</div>}
                        </td>
                        <td>
                          <div>{timeToLabel(s.start)} – {timeToLabel(s.end)}</div>
                          {s.casual.workPosition && <div className="sv-td-sub sv-position">{s.casual.workPosition}</div>}
                        </td>
                        <td>
                          <div>{s.location?.suburb}</div>
                          <div className="sv-td-sub">{s.radius} km from {s.homeBase?.suburb || 'home'}</div>
                        </td>
                        <td>{s.casual.wwc ? <span className="sv-yes">Yes</span> : <span className="sv-no">No</span>}</td>
                        <td>{s.pendingJobs} pending · {msgCountFor(s)} message{msgCountFor(s) === 1 ? '' : 's'}</td>
                        <td>
                          <span className={`ca-avail-status ${s.status === 'Assigned' ? 'assigned' : 'open'}`}>{s.status === 'Assigned' ? 'Offered · awaiting reply' : 'Open'}</span>
                          {historyFor(s).length > 0 && <div className="sv-td-sub">{historyFor(s).length} job{historyFor(s).length === 1 ? '' : 's'} on this day</div>}
                        </td>
                      </tr>
                      <tr className="sv-tr-foot" onClick={() => setAssigning({ slot: s })}>
                        <td colSpan={6}>
                          <span className="ca-view-link">
                            Click here to assign job
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                          </span>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
          <div className="sv-slots">
            {list.map((s) => (
              <div key={s.id} className={`sv-slot ${s.status === 'Assigned' ? 'sv-slot-assigned' : ''}`}>
                <div className="sv-slot-head">
                  <div className="sv-slot-name">
                    {s.casual.name || 'Casual'}
                    {s.casual.wwc && <span className="ca-wwc">WWCC</span>}
                  </div>
                  {s.casual.mobile && <a className="sv-tel" href={telHref(s.casual.mobile)}>{s.casual.mobile}</a>}
                </div>
                <div className="sv-slot-grid">
                  <div><span className="ca-label">Available</span>{timeToLabel(s.start)} – {timeToLabel(s.end)}{s.casual.workPosition && <div className="sv-td-sub sv-position">{s.casual.workPosition}</div>}</div>
                  <div><span className="ca-label">Works from</span>{s.location?.suburb}</div>
                  <div><span className="ca-label">Home – Job</span>{s.radius} km{s.homeBase?.suburb ? ` from ${s.homeBase.suburb}` : ''}</div>
                  <div><span className="ca-label">Jobs</span>{s.pendingJobs} pending · {msgCountFor(s)} message{msgCountFor(s) === 1 ? '' : 's'}</div>
                </div>
                <div className="sv-slot-foot">
                  <span className={`ca-avail-status ${s.status === 'Assigned' ? 'assigned' : 'open'}`}>{s.status === 'Assigned' ? 'Offered · awaiting reply' : 'Open'}</span>
                  <button type="button" className="ca-btn solid" style={{ background: ACCENT }} onClick={() => setAssigning({ slot: s })}>Assign job</button>
                </div>
              </div>
            ))}
          </div>
          )}
        </section>
      ))}


      {matching && (
        <MatchAvailabilityModal
          slots={searchSlots}
          liveJobs={liveJobs}
          pipelineJobs={snlJobs}
          onClose={() => setMatching(false)}
          onAssign={(slot, payload) => assign(slot.id, payload)}   /* straight to the server; the board stays open */
          onEditNotes={editSnlJob}
        />
      )}
      {assigning && (
        <AssignModal
          key={`${assigning.slot.id}-${assigning.job?.id || ''}`}
          slot={assigning.slot}
          initialJob={assigning.job || null}
          onClose={() => setAssigning(null)}
          onAssign={assign}
          pipelineJobs={snlJobs}
          history={historyFor(assigning.slot)}
          onOpenHistory={setHistoryDetail}
        />
      )}
      {historyDetail && <JobDetailModal j={historyDetail} onClose={() => setHistoryDetail(null)} />}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Tab 2 — Responses
   ───────────────────────────────────────────────────────────── */
export const SuperResponses = () => {
  const { responses, responsesLoading, act, refreshJobs, busyIds, threads } = useSuper();
  const msgCount = (id) => ((threads || []).find((t) => t.assignmentId === id)?.messages?.length) || 0;
  const [detail, setDetail] = useState(null);
  const [openId, setOpenId] = useState(null);                    // folded by default; ONE open at a time
  const toggleFold = (id) => setOpenId((cur) => (cur === id ? null : id));   // opening one closes the other
  const groups = [
    { id: 'accepted', title: 'Accepted — rostered; press Approve once the job is done' },
    { id: 'pending', title: 'Pending — offered, waiting for the casual' },
    { id: 'declined', title: 'Declined by the casual' },
    { id: 'cancelled', title: 'Withdrawn by a supervisor' },
  ];
  return (
    <div className="sv-pane">
      <div className="sv-toolbar sv-toolbar-right"><button type="button" className="ca-btn ghost" onClick={refreshJobs}>Refresh</button></div>
      {responsesLoading && responses.length === 0 && <div className="ca-skeleton" />}
      {!responsesLoading && responses.length === 0 && (
        <div className="ca-empty"><div className="ca-empty-title">Nothing waiting</div><p>Jobs you assign show up here until they're approved or cancelled.</p></div>
      )}
      {groups.map((g) => {
        const list = responses.filter((j) => j.status === g.id);
        if (!list.length) return null;
        const open = openId === g.id;
        const rows = (open ? list : list.slice(0, FOLD_PEEK)).map((j) => <JobRow key={j.id} j={j} busy={busyIds[j.id] || ''} onOpen={setDetail} onAction={act} showApprove msgCount={msgCount(j.id)} />);
        return (
          <FoldGroup key={g.id} title={g.title} count={list.length} accent={ACCENT} open={open} onToggle={() => toggleFold(g.id)}>
            <div className="sv-roster">{rows}</div>
          </FoldGroup>
        );
      })}
      {detail && <JobDetailModal j={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Tab 3 — Roster
   ───────────────────────────────────────────────────────────── */
export const SuperRoster = () => {
  const { roster, rosterLoading, act, refreshJobs, today, busyIds, threads, openMessage } = useSuper();
  const msgCount = (id) => ((threads || []).find((t) => t.assignmentId === id)?.messages?.length) || 0;
  const [detail, setDetail] = useState(null);
  // one fold per MONTH, most recent first; inside, the rows keep their day heading (JobRow shows
  // no date of its own). Folded by default, ONE open at a time.
  const [openMonth, setOpenMonth] = useState(null);
  const byMonth = useMemo(() => {
    const days = new Map();
    roster.forEach((j) => { if (!days.has(j.date)) days.set(j.date, []); days.get(j.date).push(j); });
    const months = new Map();
    [...days.entries()].sort((a, b) => b[0].localeCompare(a[0])).forEach(([date, list]) => {     // most recent day first
      const key = (date || '').slice(0, 7) || 'No date';
      if (!months.has(key)) months.set(key, []);
      list.forEach((j, i) => months.get(key).push({ j, date, firstOfDay: i === 0, dayCount: list.length }));
    });
    return [...months.entries()].map(([key, rows]) => ({
      key, rows,
      label: key === 'No date' ? 'No date' : new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
    }));
  }, [roster]);
  const dayHead = (r) => (
    <h3 className="sv-day-title sv-roster-day">{r.date === today ? 'Today · ' : ''}{fmtShort(r.date)}<span className="sv-day-count">{r.dayCount} job{r.dayCount === 1 ? '' : 's'}</span></h3>
  );
  return (
    <div className="sv-pane">
      <div className="sv-toolbar sv-toolbar-right"><button type="button" className="ca-btn ghost" onClick={refreshJobs}>Refresh</button></div>
      {rosterLoading && roster.length === 0 && <div className="ca-skeleton" />}
      {!rosterLoading && byMonth.length === 0 && (
        <div className="ca-empty"><div className="ca-empty-title">No completed jobs yet</div><p>When a job is done, press Approve on the Responses tab and it lands here under its day.</p></div>
      )}
      {byMonth.map((m) => {
        const open = openMonth === m.key;
        const rows = open ? m.rows : m.rows.slice(0, FOLD_PEEK);
        return (
          <FoldGroup key={m.key} title={m.label} count={m.rows.length} accent={ACCENT} open={open} onToggle={() => setOpenMonth((cur) => (cur === m.key ? null : m.key))}>
            <div className="sv-roster">
              {rows.map((r) => (
                <React.Fragment key={r.j.id}>
                  {r.firstOfDay && dayHead(r)}
                  <JobRow j={r.j} busy={busyIds[r.j.id] || ''} onOpen={setDetail} onAction={act} showApprove={false}
                          allowWithdraw={false} msgCount={msgCount(r.j.id)} onMessage={openMessage} />   {/* no Withdraw on the roster — message the casual instead */}
                </React.Fragment>
              ))}
            </div>
          </FoldGroup>
        );
      })}
      {detail && <JobDetailModal j={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Tab — Messages (one thread per assignment, all casuals)
   ───────────────────────────────────────────────────────────── */
export const SuperMessages = () => {
  const { threads, threadsLoading, me, sendMessage, openThread, refreshThreads } = useSuper();
  return (
    <div className="sv-pane">
      <MessagesPanel threads={threads} loading={threadsLoading} me={me} onSend={sendMessage} onOpen={openThread} onRefresh={refreshThreads} accent={ACCENT} showCasual side="super" />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Provider
   ───────────────────────────────────────────────────────────── */
export const SupervisorProvider = ({ children }) => {
  useStylesheets([cssUrl('casualsAvailability.css'), cssUrl('supervisorAvailability.css')]);
  const today = melTodayISO();

  const [slots, setSlots] = useState([]);
  const [responses, setResponses] = useState([]);
  const [roster, setRoster] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [responsesLoading, setResponsesLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [snlJobs, setSnlJobs] = useState([]);
  const [snlJobsLoading, setSnlJobsLoading] = useState(true);
  const { toasts, push, pushEmail, fail, dismiss } = useToasts();

  const showToast = (m) => push(m);

  const refreshSlots = () => { setSlotsLoading(true); return fetchSuperSlots().then((r) => setSlots(r.slots || [])).catch(fail).finally(() => setSlotsLoading(false)); };
  const refreshJobs = () => {
    setResponsesLoading(true); setRosterLoading(true);
    return Promise.all([
      fetchSuperJobs('responses').then((r) => setResponses(r.jobs || [])),
      fetchSuperJobs('roster').then((r) => setRoster(r.jobs || [])),
    ]).catch(fail).finally(() => { setResponsesLoading(false); setRosterLoading(false); });
  };

  const refreshSnlJobs = () => { setSnlJobsLoading(true); return fetchSnlJobs().then((r) => setSnlJobs(r.jobs || [])).catch(fail).finally(() => setSnlJobsLoading(false)); };

  // a day whose job the cleaner has ACCEPTED (or that is done) leaves the Open Slots board;
  // a Pending offer keeps the day visible until the cleaner answers. The board, the Match
  // board and the Open Slots tab count all read this one list.
  const takenDays = useMemo(() => {
    const ids = new Set();
    [...responses, ...roster].forEach((j) => { if (['accepted', 'approved'].includes(j.status) && j.availabilityId) ids.add(j.availabilityId); });
    return ids;
  }, [responses, roster]);
  const openSlots = useMemo(() => slots.filter((s) => !takenDays.has(s.id)), [slots, takenDays]);

  // mount-only first load. These three are recreated on every render, so listing them as
  // dependencies would refetch on every render; later refreshes are triggered explicitly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshSlots(); refreshJobs(); refreshSnlJobs(); }, []);
  useEffect(() => () => { document.body.style.overflow = ''; }, []);   // never leave the page locked

  const createSnlJob = async (payload) => {
    const r = await createSnlJobApi(payload);        // throws with the server's reason
    showToast(r.job?.replay ? 'Already added a moment ago' : 'Added to Jobs Available');
    await refreshSnlJobs();
    return r.job;
  };
  const resync = async () => {
    try {
      const r = await resyncStatuses();
      showToast(`Statuses checked — ${r.jobs} job${r.jobs === 1 ? '' : 's'}, ${r.days} day${r.days === 1 ? '' : 's'} corrected`);
      await Promise.all([refreshSnlJobs(), refreshSlots(), refreshJobs()]);
    } catch (e) { fail(e); }
  };
  const reopenSnlJob = async (id) => {
    try { await patchSnlJobStatus(id, 'Available'); showToast('Job reopened — available to assign'); await refreshSnlJobs(); } catch (e) { fail(e); }
  };
  const closeSnlJob = async (id, reason = 'Job closed') => {
    try {
      const r = await patchSnlJobStatus(id, 'Closed', reason);
      const n = r.job?.cancelledAssignments || 0;
      showToast(n ? `Job withdrawn — ${n} assignment${n === 1 ? '' : 's'} cancelled` : 'Job withdrawn');
      await Promise.all([refreshSnlJobs(), refreshSlots(), refreshJobs()]);
    } catch (e) { fail(e); }
  };
  // Edit a pipeline job's hours / notes (Create Jobs record). Throws with the server's reason —
  // the record shows it inline. Live assignments follow the change on the server.
  const editSnlJob = async (id, fields) => {
    const r = await patchSnlJobFields(id, fields);
    const n = r.job?.updatedAssignments || 0;
    showToast(n ? `Job updated — ${n} assignment${n === 1 ? '' : 's'} updated too` : 'Job updated');
    await Promise.all([refreshSnlJobs(), refreshJobs()]);
    return r.job;
  };

  const assign = async (availabilityId, form) => {
    const r = await assignSuperJob({ availabilityId, ...form });   // throws with the server's reason
    showToast('Job sent to the casual');
    pushEmail(r.email);                          // "Email sent to … for Potential Job Offered"
    await Promise.all([refreshSlots(), refreshJobs(), refreshSnlJobs()]);
    return r.job;
  };

  const { user } = useUser();
  const me = user?.userName || '';
  const [busyIds, setBusyIds] = useState({});         // jobId → 'approve' | 'cancel' while in flight
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [messagingJob, setMessagingJob] = useState(null);   // job whose thread is open from a record
  const [msgOpen, setMsgOpen] = useState(false);            // the Job Messages list modal
  const loadThreads = () => {
    setThreadsLoading(true);
    return fetchSuperThreads().then((r) => setThreads(r.threads || [])).catch(() => {}).finally(() => setThreadsLoading(false));
  };
  // Poll only while the tab is visible: 30 s while someone is doing things here, easing off to
  // 5 min when idle, nothing at all when hidden (one immediate read when it comes back). Every
  // deliberate refresh — mount, the Refresh button, after a send — counts as activity.
  const pollBump = usePollWhenVisible(loadThreads);
  const refreshThreads = () => { pollBump(); return loadThreads(); };
  useEffect(() => { refreshThreads(); }, []);
  const sendMessage = async (assignmentId, text) => {
    const r = await sendSuperMessage(assignmentId, text);
    pushEmail(r.email);                          // "Email sent to … for Job Message"
    const m = { sender: 'Supervisor', ...(r.message || { id: `tmp-${Date.now()}`, from: me, to: '', text, status: 'New', createdAt: new Date().toISOString() }) };
    setThreads((prev) => {
      const exists = prev.some((t) => t.assignmentId === assignmentId);
      const base = exists ? prev : [...prev, { assignmentId, casual: messagingJob?.casualName || '', jobLocation: messagingJob?.jobName || messagingJob?.jobSuburb || '', jobDate: messagingJob?.date, messages: [], unread: 0, status: 'Seen' }];
      return base.map((t) => {
        if (t.assignmentId !== assignmentId) return t;
        // by id: a replayed send returns the same message and must not double up
        if (t.messages.some((x) => x.id === m.id)) return t;
        return { ...t, messages: [...t.messages, m], lastFrom: m.from, lastTo: m.to, lastAt: m.createdAt };
      });
    });
    setTimeout(refreshThreads, 1500);
  };
  const openThread = async (assignmentId) => {
    pollBump();                                          // reading a thread = active here
    await markSuperThreadSeen(assignmentId);
    setThreads((prev) => prev.map((t) => (t.assignmentId !== assignmentId ? t : {
      ...t, unread: 0, status: 'Seen',
      messages: t.messages.map((m) => (m.sender !== 'Supervisor' ? { ...m, status: 'Seen' } : m)),
    })));
  };
  const unreadTotal = threads.reduce((n, t) => n + (t.unread || 0), 0);

  // "Send message" from any job record: open that assignment's thread, or a
  // fresh one if nothing has been sent yet. Derived from `threads` so a sent
  // message appears as soon as refreshThreads() lands.
  const openMessage = (job) => setMessagingJob(job);
  const messagingThread = useMemo(() => {
    if (!messagingJob) return null;
    const existing = threads.find((t) => t.assignmentId === messagingJob.id);
    return existing || {
      assignmentId: messagingJob.id,
      casual: messagingJob.casualName || 'Casual',
      jobLocation: messagingJob.jobSuburb || messagingJob.slotLocation || messagingJob.jobAddress || 'Job',
      jobDate: messagingJob.date,
      messages: [], unread: 0, status: 'Seen',
    };
  }, [messagingJob, threads]);

  const act = async (jobId, action, reason = '') => {
    if (busyIds[jobId]) return;                          // ignore a second press while processing
    setBusyIds((b) => ({ ...b, [jobId]: action }));
    try {
      const r = await superJobAction(jobId, action, reason);
      showToast(action === 'approve' ? 'Job marked done' : 'Job withdrawn');
      pushEmail(r.email);                        // "Email sent to … for Job Completed / Job Withdrawn"
      await Promise.all([refreshSlots(), refreshJobs(), refreshSnlJobs()]);
    } catch (e) { fail(e); }
    finally { setBusyIds((b) => { const n = { ...b }; delete n[jobId]; return n; }); }
  };

  const awaitingApproval = responses.filter((j) => j.status === 'accepted').length;

  const ctx = { today, slots, responses, roster, slotsLoading, responsesLoading, rosterLoading, assign, act, refreshSlots, refreshJobs, awaitingApproval,
    snlJobs, snlJobsLoading, refreshSnlJobs, createSnlJob, closeSnlJob, reopenSnlJob, editSnlJob, resync, openSlots, takenDays,
    busyIds, me, threads, threadsLoading, unreadTotal, sendMessage, openThread, refreshThreads, openMessage,
    msgOpen, setMsgOpen,
    push, pushEmail };
  return (
    <Ctx.Provider value={ctx}>
      {children}
      {msgOpen && <SuperMessagesModal onClose={() => setMsgOpen(false)} />}
      {messagingThread && (
        <ThreadModal thread={messagingThread} me={me} side="super" accent={ACCENT} onSend={sendMessage} onClose={() => setMessagingJob(null)} />
      )}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </Ctx.Provider>
  );
};

/* default: stacked (single-tab) fallback */
const SupervisorAvailability = () => (
  <SupervisorProvider>
    <div className="ca-page"><SuperSlots /><SuperResponses /><SuperRoster /></div>
  </SupervisorProvider>
);
export default SupervisorAvailability;